import type { ReactNode } from 'react';

/**
 * Second-generation scene vocabulary — the technical safety diagram chosen on
 * the vt-863 comparison board ("hybrid C + B": guided-steps frame outside,
 * blueprint drawing inside). Every animation scene and hotspot art is built
 * from these pieces so the 24 drawings read as one system; the class names
 * resolve in `css.ts` (`.hbB …`) against the player's `--color-*` tokens.
 *
 * Rules every scene follows:
 * - viewBox 480×280, `<Blueprint>` as the root;
 * - readable text only through the `hbB-*` type classes (17-unit floor);
 * - step-bound art wrapped in `<On step="2">` (or `"1 2"`), never timed CSS;
 * - one `<Verdict>` per step that judges something; `lim` for "limited";
 * - dimensions through `<Dim>` and figures through `hbB-dim-txt`;
 * - in-art microcopy Czech by default, English under `en`.
 */
export type SceneProps = { en: boolean };

/** Ids for the per-scene `<defs>` — SVG ids are document-global, so every scene prefixes its own. */
export function blueprintIds(id: string) {
  return {
    grid: `hbB-grid-${id}`,
    hatch: `hbB-hatch-${id}`,
    arrow: `hbB-arr-${id}`,
  };
}

/**
 * Blueprint stage: grid backdrop, hatch + arrow defs, uppercase title line at
 * the top and an optional statutory source line at the bottom. `id` must be
 * unique per scene (use the scene key).
 */
export function Blueprint({
  id,
  label,
  title,
  source,
  children,
}: {
  id: string;
  /** Accessible name of the whole drawing. */
  label: string;
  /** Top-left uppercase title (what the drawing shows). */
  title?: string;
  /** Bottom-left source line — the regulation or standard the drawing rests on. */
  source?: string;
  children: ReactNode;
}) {
  const ids = blueprintIds(id);
  return (
    <svg viewBox="0 0 480 280" role="img" aria-label={label} className="hbB">
      <defs>
        <pattern id={ids.grid} width="12" height="12" patternUnits="userSpaceOnUse">
          <path className="hbB-grid-line" d="M12 0 H0 V12" />
        </pattern>
        <pattern
          id={ids.hatch}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line className="hbB-hatch-line" x1="0" y1="0" x2="0" y2="6" />
        </pattern>
        <marker
          id={ids.arrow}
          viewBox="0 0 8 8"
          refX="7"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path className="hbB-dim-fill" d="M0 0 L8 4 L0 8 z" />
        </marker>
      </defs>
      <rect x="0" y="0" width="480" height="280" fill={`url(#${ids.grid})`} />
      {title && (
        <text className="hbB-title" x="16" y="24">
          {title}
        </text>
      )}
      {children}
      {source && (
        <text className="hbB-title" x="16" y="270">
          {source}
        </text>
      )}
    </svg>
  );
}

/** Step-bound group: drawn only while the frame is on one of the listed steps (`"2"` or `"1 2"`). */
export function On({ step, children }: { step: string; children: ReactNode }) {
  return (
    <g className="hbA-on" data-on={step}>
      {children}
    </g>
  );
}

/** Verdict badge — ✓ permitted, ✗ prohibited, — limited. Pops once when its step mounts. */
export function Verdict({
  kind,
  x,
  y,
  r = 16,
}: {
  kind: 'ok' | 'no' | 'lim';
  x: number;
  y: number;
  r?: number;
}) {
  const k = r / 16;
  return (
    <g className="hbA-verdict">
      <circle
        className={kind === 'ok' ? 'hbB-ok-fill' : kind === 'no' ? 'hbB-no-fill' : 'hbB-warn-fill'}
        cx={x}
        cy={y}
        r={r}
      />
      <g
        fill="none"
        stroke="#fff"
        strokeWidth={3.2 * k}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {kind === 'ok' && <path d={`M${x - 7 * k} ${y} l${5 * k} ${5 * k} l${9 * k} -${10 * k}`} />}
        {kind === 'no' && (
          <path
            d={`M${x - 6 * k} ${y - 6 * k} l${12 * k} ${12 * k} M${x + 6 * k} ${y - 6 * k} l-${12 * k} ${12 * k}`}
          />
        )}
        {kind === 'lim' && <path d={`M${x - 7 * k} ${y} h${14 * k}`} />}
      </g>
    </g>
  );
}

/** Dimension line with arrowheads on both ends (needs the scene's `blueprintIds(id).arrow`). */
export function Dim({
  arrow,
  x1,
  y1,
  x2,
  y2,
}: {
  arrow: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}) {
  return (
    <line
      className="hbB-dim"
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      markerStart={`url(#${arrow})`}
      markerEnd={`url(#${arrow})`}
    />
  );
}

/** Hatched solid (ground, wall, masonry) with a heavy edge on the side facing the drawing. */
export function Solid({
  hatch,
  x,
  y,
  width,
  height,
  edge,
}: {
  hatch: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Which side gets the heavy outline. */
  edge: 'top' | 'bottom' | 'left' | 'right';
}) {
  const line =
    edge === 'top'
      ? { x1: x, y1: y, x2: x + width, y2: y }
      : edge === 'bottom'
        ? { x1: x, y1: y + height, x2: x + width, y2: y + height }
        : edge === 'left'
          ? { x1: x, y1: y, x2: x, y2: y + height }
          : { x1: x + width, y1: y, x2: x + width, y2: y + height };
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={`url(#${hatch})`} />
      <line className="hbB-draw-heavy" {...line} />
    </g>
  );
}

/** Panel card with a numbered corner — the three-column "1 · 2 · 3" comparison layout. */
export function Panel({
  x,
  y,
  width,
  height,
  n,
  tone,
  children,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  n?: number;
  tone?: 'ok' | 'no';
  children: ReactNode;
}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect
        className={tone === 'ok' ? 'hbB-ok-soft' : tone === 'no' ? 'hbB-no-soft' : 'hbB-card'}
        stroke="var(--color-line-strong, #d7cbb4)"
        strokeWidth="1"
        x="0"
        y="0"
        width={width}
        height={height}
        rx="6"
      />
      {n !== undefined && (
        <text className="hbB-num" x="12" y="32">
          {n}
        </text>
      )}
      {children}
    </g>
  );
}
